import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Facebook, Twitter, Link2, Loader2, Share2 } from "lucide-react";
import { isNativeApp } from "@/lib/platform";

/** On native (iOS/Android), open the OS-level Share Sheet so the user can
 * pick their installed Facebook, Messenger, WhatsApp, X (Twitter),
 * Instagram, Email, etc. On web, fall through to the classic
 * facebook.com/sharer or twitter.com/intent popup.
 *
 * `channel` is only used on web to pick which fallback URL to open — on
 * native the OS decides based on which apps the user has.
 */
async function openShare({ channel, url, text }) {
  if (isNativeApp()) {
    try {
      const { Share } = await import("@capacitor/share");
      const dialogTitle = channel === "facebook"
        ? "Share to Facebook"
        : channel === "twitter"
        ? "Share to X"
        : "Share";
      await Share.share({
        title: "PrepMasters",
        text,
        url,
        dialogTitle,
      });
      return;
    } catch (e) {
      // If user cancels the sheet, Capacitor throws — swallow silently.
      if (String(e?.message || "").toLowerCase().includes("cancel")) return;
      // Fall through to browser open if the plugin fails for any reason.
    }
    try {
      const { Browser } = await import("@capacitor/browser");
      const webUrl = channel === "facebook"
        ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`
        : channel === "twitter"
        ? `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
        : url;
      await Browser.open({ url: webUrl, presentationStyle: "popover" });
      return;
    } catch { /* noop */ }
  }
  // Web fallback — classic sharer popup.
  const webUrl = channel === "facebook"
    ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`
    : channel === "twitter"
    ? `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
    : url;
  if (typeof window !== "undefined") {
    window.open(webUrl, "_blank", "width=600,height=500,noopener,noreferrer");
  }
}

/** Generic Facebook / X / Copy-link share row.
 *
 * `getShareUrl` is an async function that returns the public share URL (or
 * may throw). The button calls it lazily on click so we don't create a share
 * token until the student actually wants to share.
 *
 * Variants:
 *   - "row": horizontal pill buttons (used in cards/results pages)
 *   - "compact": small icon-only row (used inside badge tiles)
 */
export default function ShareButtons({
  getShareUrl,
  defaultText = "Check out my progress on PrepMaster!",
  variant = "row",
  testIdPrefix = "share",
  className = "",
}) {
  const [busy, setBusy] = useState(false);

  const open = async (channel) => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await getShareUrl();
      // Support both legacy `url` strings and new `{url, newly_awarded_badge}` objects
      const url = typeof result === "string" ? result : result?.url;
      const awarded = typeof result === "object" ? result?.newly_awarded_badge : null;
      if (!url) throw new Error("Couldn't build the share link");

      if (channel === "facebook") {
        await openShare({ channel: "facebook", url, text: defaultText });
      } else if (channel === "twitter") {
        await openShare({ channel: "twitter", url, text: defaultText });
      } else if (channel === "copy") {
        try {
          await navigator.clipboard.writeText(url);
          toast.success("Link copied — paste it anywhere!");
        } catch {
          // Fallback for browsers without clipboard permission
          window.prompt("Copy this link:", url);
        }
      }
      // Celebrate the Top Sharer badge if it was just unlocked
      if (awarded?.id) {
        toast.success(`🏆 Badge unlocked: ${awarded.name}!`, {
          description: awarded.description || "You're the weekly Top Sharer.",
          duration: 7000,
        });
      }
    } catch (e) {
      toast.error(e?.message || "Couldn't open share dialog");
    } finally {
      setBusy(false);
    }
  };

  if (variant === "compact") {
    return (
      <div className={`flex items-center gap-1.5 ${className}`} data-testid={`${testIdPrefix}-row`}>
        <button
          type="button"
          onClick={() => open("facebook")}
          disabled={busy}
          className="h-8 w-8 grid place-items-center rounded-full bg-[#1877F2] text-white hover:opacity-90 transition disabled:opacity-50"
          title="Share on Facebook"
          data-testid={`${testIdPrefix}-fb`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Facebook className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button"
          onClick={() => open("twitter")}
          disabled={busy}
          className="h-8 w-8 grid place-items-center rounded-full bg-slate-900 text-white hover:opacity-90 transition disabled:opacity-50"
          title="Share on X"
          data-testid={`${testIdPrefix}-x`}
        >
          <Twitter className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => open("copy")}
          disabled={busy}
          className="h-8 w-8 grid place-items-center rounded-full bg-slate-200 text-slate-700 hover:bg-slate-300 transition disabled:opacity-50"
          title="Copy link"
          data-testid={`${testIdPrefix}-copy`}
        >
          <Link2 className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className={`flex flex-wrap gap-2 ${className}`} data-testid={`${testIdPrefix}-row`}>
      <Button
        type="button"
        onClick={() => open("facebook")}
        disabled={busy}
        className="bg-[#1877F2] hover:bg-[#0E5BC4] text-white"
        data-testid={`${testIdPrefix}-fb`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Facebook className="w-4 h-4 mr-1.5" />}
        Share on Facebook
      </Button>
      <Button
        type="button"
        onClick={() => open("twitter")}
        disabled={busy}
        className="bg-slate-900 hover:bg-black text-white"
        data-testid={`${testIdPrefix}-x`}
      >
        <Twitter className="w-4 h-4 mr-1.5" />
        Share on X
      </Button>
      <Button
        type="button"
        variant="outline"
        onClick={() => open("copy")}
        disabled={busy}
        data-testid={`${testIdPrefix}-copy`}
      >
        <Link2 className="w-4 h-4 mr-1.5" />
        Copy Link
      </Button>
    </div>
  );
}

/** Convenience wrapper for callers that don't need the lazy getShareUrl pattern.
 * Pass a known URL string directly.
 */
export function ShareInlineRow({ url, defaultText, testIdPrefix = "share-inline" }) {
  return (
    <ShareButtons
      getShareUrl={() => Promise.resolve(url)}
      defaultText={defaultText}
      testIdPrefix={testIdPrefix}
    />
  );
}

export { Share2 };
